/**
 * Convertit du HTML basique en texte (ou markdown inline minimal) :
 *  - <a href="X">label</a> → [label](X)
 *  - <br>, </p>, </div>, </li>, </h1-6> → saut de ligne
 *  - <strong>/<b> → **...**, <em>/<i> → *...*
 *  - autres balises supprimées, entités HTML décodées
 *  - <wbr>, espaces multiples normalisés
 *
 * À utiliser pour tout champ libre (descriptions d'événements, notes,
 * notifications, rapports) afin de garantir qu'aucune balise brute
 * (`<strong>`, `<a …>`, `<wbr/>`, &amp;) n'apparaisse dans l'UI.
 *
 * Observabilité : passer un `source` (ex. "exportProspects:notes") déclenche
 * un `console.warn` + incrément du compteur global lorsque l'entrée contient
 * du HTML brut. Ces stats sont accessibles via `getHtmlSanitizationStats()`
 * et `window.__htmlSanitizationStats` afin de remonter aux sources de données
 * non nettoyées (formulaires manquants, imports, IA, etc.).
 */

export interface HtmlSanitizationStats {
  total: number;
  bySource: Record<string, number>;
  samples: Array<{ source: string; sample: string; at: string }>;
}

const SAMPLE_LIMIT = 50;

const stats: HtmlSanitizationStats = {
  total: 0,
  bySource: {},
  samples: [],
};

// Expose pour l'inspection en console (DevTools) et les outils d'audit.
if (typeof window !== "undefined") {
  (window as any).__htmlSanitizationStats = stats;
}

export function getHtmlSanitizationStats(): HtmlSanitizationStats {
  return stats;
}

export function resetHtmlSanitizationStats(): void {
  stats.total = 0;
  stats.bySource = {};
  stats.samples = [];
}

function recordHtmlHit(source: string, original: string): void {
  stats.total += 1;
  stats.bySource[source] = (stats.bySource[source] ?? 0) + 1;
  if (stats.samples.length < SAMPLE_LIMIT) {
    stats.samples.push({
      source,
      sample: original.slice(0, 200),
      at: new Date().toISOString(),
    });
  }
  // Visible en DevTools — un log par occurrence pour pouvoir tracer la pile
  // d'appels jusqu'à la source de données non nettoyée.
  // eslint-disable-next-line no-console
  console.warn(
    `[stripHtml] HTML détecté dans « ${source} » (total: ${stats.total})`,
    { sample: original.slice(0, 200) },
  );
}

export function stripHtml(
  input: string | null | undefined,
  source: string = "unknown",
): string {
  if (!input) return "";
  const hasHtml = /<[a-z!/]/i.test(input) || /&[#a-z0-9]+;/i.test(input);
  if (!hasHtml) return input;
  recordHtmlHit(source, input);
  let s = input;
  // liens → markdown
  s = s.replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (_m, href, label) => {
    const cleanLabel = label.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return `[${cleanLabel || href}](${href})`;
  });
  // gras / italique
  s = s.replace(/<\/?(strong|b)\b[^>]*>/gi, "**");
  s = s.replace(/<\/?(em|i)\b[^>]*>/gi, "*");
  // sauts de ligne
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/(p|div|li|h[1-6])>/gi, "\n");
  // suppression du reste des balises
  s = s.replace(/<[^>]+>/g, "");
  // entités HTML courantes
  s = s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(parseInt(d, 10)));
  // normalisation espaces
  s = s.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  return s;
}

export default stripHtml;
